// Copyright (c) Ping. All rights reserved.

import React, { useCallback } from 'react';
import { Pressable, Text, View, type TextStyle, type ViewStyle } from 'react-native';
import { tokens } from '../../design-system/tokens';
import { useColorScheme } from '../../foundations/colorScheme';

export interface SettingsToggleProps {
  readonly label: string;
  readonly description?: string;
  readonly value: boolean;
  readonly disabled?: boolean;
  readonly onChange: (next: boolean) => void;
}

export const SettingsToggle: React.FC<SettingsToggleProps> = ({
  label,
  description,
  value,
  disabled = false,
  onChange,
}) => {
  const scheme = useColorScheme();
  const colors = tokens.color.semantic[scheme];

  const handlePress = useCallback(() => {
    if (!disabled) {
      onChange(!value);
    }
  }, [disabled, onChange, value]);

  const trackColor = value ? colors.accent.indigo : colors.border.primary;
  const thumbTranslate = value ? 22 : 2;

  return (
    <Pressable
      style={styles.row}
      onPress={handlePress}
      disabled={disabled}
      accessibilityRole="switch"
      accessibilityLabel={label}
      accessibilityState={{ checked: value, disabled }}
    >
      <View style={styles.copy}>
        <Text style={[styles.label, { color: colors.text.primary }]}>{label}</Text>
        {description ? (
          <Text style={[styles.description, { color: colors.text.secondary }]}>
            {description}
          </Text>
        ) : null}
      </View>
      <View
        style={[
          styles.track,
          { backgroundColor: trackColor, opacity: disabled ? 0.5 : 1 },
        ]}
      >
        <View
          style={[
            styles.thumb,
            {
              transform: [{ translateX: thumbTranslate }],
              backgroundColor: colors.surface.primary,
            },
          ]}
        />
      </View>
    </Pressable>
  );
};

const styles = {
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: tokens.spacing.sm,
    gap: tokens.spacing.md,
  } satisfies ViewStyle,
  copy: {
    flexShrink: 1,
    gap: tokens.spacing.xxs,
  } satisfies ViewStyle,
  label: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.body,
    fontWeight: '500' as TextStyle['fontWeight'],
  } satisfies TextStyle,
  description: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
  } satisfies TextStyle,
  track: {
    width: 44,
    height: 26,
    borderRadius: tokens.radius.pill,
    justifyContent: 'center',
  } satisfies ViewStyle,
  thumb: {
    width: 22,
    height: 22,
    borderRadius: tokens.radius.pill,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  } satisfies ViewStyle,
};
